import { useCallback, useEffect, useState } from "react";
import { Download, FileText } from "lucide-react";
import { api, money, fmtDate } from "@/lib/api";
import { downloadCsv } from "@/lib/csv";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Drill-down behind the CEO's HQ revenue/profit KPIs. `focus` = "revenue" | "profit".
export function HqBreakdownDialog({ open, onOpenChange, focus = "revenue" }) {
  const [studios, setStudios] = useState([]);
  const [studioId, setStudioId] = useState("all");
  const [product, setProduct] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState({ rows: [], count: 0, total_revenue: 0, total_profit: 0 });

  useEffect(() => {
    if (open) api.get("/studios").then(({ data }) => setStudios(data)).catch(() => {});
  }, [open]);

  const load = useCallback(() => {
    if (!open) return;
    const params = {};
    if (studioId !== "all") params.studio_id = studioId;
    if (product.trim()) params.product = product.trim();
    if (from) params.from_date = from;
    if (to) params.to_date = to;
    api.get("/reports/hq/breakdown", { params }).then(({ data }) => setData(data)).catch(() => {});
  }, [open, studioId, product, from, to]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    downloadCsv(`hq-sales-breakdown-${new Date().toISOString().slice(0, 10)}.csv`,
      data.rows.map((r) => ({
        Date: r.date, Type: r.type_label, "Studio / Detailer": r.party, Product: r.product,
        Rolls: r.rolls ?? "", Sqft: r.sqft ?? "", Quantity: `${r.quantity} ${r.unit}`,
        "Amount (INR)": r.amount, "Profit (INR)": r.profit,
      })));
  };

  const exportPdf = () => {
    const p = new URLSearchParams();
    if (studioId !== "all") p.set("studio_id", studioId);
    if (product.trim()) p.set("product", product.trim());
    if (from) p.set("from_date", from);
    if (to) p.set("to_date", to);
    window.open(`${BACKEND}/api/reports/hq/breakdown.pdf?${p.toString()}`, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-5xl overflow-y-auto" data-testid="hq-breakdown-dialog">
        <DialogHeader>
          <DialogTitle>HQ sales breakdown</DialogTitle>
          <DialogDescription>
            Every studio sale and detailer purchase behind your HQ revenue and profit — all orders except returns/cancellations.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div>
            <Label>Studio</Label>
            <Select value={studioId} onValueChange={setStudioId}>
              <SelectTrigger data-testid="hq-bd-studio"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All studios</SelectItem>
                {studios.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Product</Label>
            <Input data-testid="hq-bd-product" placeholder="e.g. PPF" value={product}
              onChange={(e) => setProduct(e.target.value)} />
          </div>
          <div>
            <Label>From</Label>
            <Input type="date" data-testid="hq-bd-from" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <Label>To</Label>
            <Input type="date" data-testid="hq-bd-to" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
        </div>

        <div className="my-4 flex flex-wrap items-center gap-3">
          <Badge className={`border-0 px-3 py-1 text-sm ${focus === "revenue" ? "bg-brand text-white" : "bg-slate-100 text-slate-700"}`} data-testid="hq-bd-total-revenue">
            Revenue: {money(data.total_revenue)}
          </Badge>
          <Badge className={`border-0 px-3 py-1 text-sm ${focus === "profit" ? "bg-emerald-600 text-white" : "bg-emerald-50 text-emerald-700"}`} data-testid="hq-bd-total-profit">
            Profit: {money(data.total_profit)}
          </Badge>
          <span className="text-sm text-slate-500">{data.count} line(s)</span>
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" data-testid="hq-bd-export-csv" onClick={exportCsv} disabled={!data.rows.length}>
              <Download className="mr-2 h-4 w-4" /> CSV
            </Button>
            <Button size="sm" variant="outline" data-testid="hq-bd-export-pdf" onClick={exportPdf} disabled={!data.rows.length}>
              <FileText className="mr-2 h-4 w-4" /> PDF
            </Button>
          </div>
        </div>

        {data.rows.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-500">No sales match these filters.</p>
        ) : (
          <Table data-testid="hq-breakdown-table">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Studio / Detailer</TableHead>
                <TableHead>Product</TableHead>
                <TableHead className="text-right">Rolls</TableHead>
                <TableHead className="text-right">Sqft</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map((r, i) => (
                <TableRow key={i} data-testid={`hq-bd-row-${i}`}>
                  <TableCell className="whitespace-nowrap">{fmtDate(r.date)}</TableCell>
                  <TableCell>
                    <Badge className={`border-0 ${r.type === "b2b" ? "bg-violet-50 text-violet-700" : "bg-sky-50 text-sky-700"}`}>
                      {r.type_label}
                    </Badge>
                  </TableCell>
                  <TableCell>{r.party}</TableCell>
                  <TableCell>{r.product}</TableCell>
                  <TableCell className="text-right">{r.rolls ?? "—"}</TableCell>
                  <TableCell className="text-right">{r.sqft ?? "—"}</TableCell>
                  <TableCell className="text-right font-medium">{money(r.amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}
